import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { KpiCard } from './KpiCard';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Activity, Flame, Users, Percent } from 'lucide-react';
import type { UsageMetrics } from '@/hooks/useExecutiveUsageMetrics';

interface Props {
  data?: UsageMetrics;
  isLoading: boolean;
}

export function EngagementTab({ data, isLoading }: Props) {
  if (isLoading || !data) {
    return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-72" /></div>;
  }

  const e = data.engagement;
  const stickiness = e.mau > 0 ? Math.round((e.dau / e.mau) * 100) : 0;
  const reach = e.total_usuarios > 0 ? Math.round((e.mau / e.total_usuarios) * 100) : 0;

  const activeChart = [
    { name: 'DAU (24h)', value: e.dau },
    { name: 'WAU (7d)', value: e.wau },
    { name: 'MAU (30d)', value: e.mau },
    { name: 'Registrados', value: e.total_usuarios },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Usuarios activos hoy" value={e.dau.toLocaleString('es-CO')} hint={`WAU ${e.wau} · MAU ${e.mau}`} icon={<Activity className="w-4 h-4" />} accent="primary" />
        <KpiCard label="Stickiness (DAU/MAU)" value={`${stickiness}%`} hint="Frecuencia de retorno" icon={<Percent className="w-4 h-4" />} accent="accent" />
        <KpiCard label="Alcance mensual" value={`${reach}%`} hint={`${e.mau} de ${e.total_usuarios} usuarios`} icon={<Users className="w-4 h-4" />} accent="info" />
        <KpiCard label="Racha promedio" value={`${e.racha_prom} días`} hint={`Máxima histórica: ${e.racha_max}`} icon={<Flame className="w-4 h-4" />} accent="warning" />
      </div>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base">Usuarios activos vs. base registrada</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={activeChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="value" name="Usuarios" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground mt-3">
            Actividad calculada sobre los eventos registrados en la plataforma (inicios de sesión, contenidos, proyectos y entregables).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
